import type {
  Polygon as MartinezPolygon,
  MultiPolygon as MartinezMultiPolygon,
  Ring as MartinezRing,
  Geometry as MartinezGeometry,
} from 'martinez-polygon-clipping';
import { safeMartinezDiff } from '@shared/helpers/geometry/martinez-polygon-clipping-ops.helpers';
import { ActivityType } from '@shared/models/design/activities/activity-type.model';
import { hexToRgbArray } from '@shared/helpers/color.helpers';
import { getActivityTypeColor } from '@shared/helpers/design/activities.helpers';
import type {
  ActivityReference,
  Shape,
  Storey,
} from '@shared/models/design/storey.model';
import type { Polygon2D } from '@shared/models/geometry.model';
import { getMezzanineParent } from '@shared/helpers/design/storey.helpers';
import {
  isPointInPolygon,
  isPolygon2D,
  segmentsIntersect,
} from '@shared/helpers/geometry/geometry.helpers';
import {
  getCutoutShapes,
  getShapeCenter,
  getSolidShapes,
} from '@shared/helpers/design/cutout.helpers';

/**
 * Resolve the dominant activity type by share (fallback to Other).
 * @param activityRefs Activity references on the storey
 * @returns Dominant activity type
 */
export const getDominantActivityType = (
  activityRefs: ActivityReference[],
): ActivityType => {
  if (!activityRefs.length) return ActivityType.Other;
  const fallbackShare = 1 / activityRefs.length;
  let maxShare = -1;
  let dominant = ActivityType.Other;

  for (const activity of activityRefs) {
    const share = activity.share ?? fallbackShare;
    // Pick the highest share to represent primary usage.
    if (share > maxShare) {
      maxShare = share;
      dominant = activity.type;
    }
  }

  return dominant;
};

/**
 * Resolve the floor color based on activity shares.
 * @param storeys All storeys in the design
 * @param storey Storey to resolve color for
 * @returns RGB array (0-1)
 */
export const resolveStoreyFloorRgb = (
  storeys: Storey[],
  storey: Storey,
): [number, number, number] => {
  const activities =
    storey.mezzanine && storey.activities.length === 0
      ? (getMezzanineParent(storeys, storey)?.activities ?? storey.activities)
      : storey.activities;
  const dominantType = getDominantActivityType(activities);
  const color = getActivityTypeColor(dominantType);
  return hexToRgbArray(color);
};

/**
 * Resolve floor shapes; mezzanines inherit from parent storey.
 * @param storeys All storeys in the design
 * @param storey Storey to render
 * @returns Shapes to render
 */
export const resolveStoreyFloorShapes = (
  storeys: Storey[],
  storey: Storey,
): Shape[] => {
  if (storey.shapes.length > 0) return getSolidShapes(storey);
  if (!storey.mezzanine) return storey.shapes;
  const parent = getMezzanineParent(storeys, storey);
  return getSolidShapes(parent);
};

export type RoofPolygon = {
  id: string;
  rings: Polygon2D[];
};

/**
 * True when a {@link MartinezGeometry} is a single Polygon (not MultiPolygon).
 * Both nest arrays; the distinction is depth to the first coordinate:
 * - Polygon: `g[0]` is a {@link MartinezRing}, so `g[0][0]` is a Point and `g[0][0][0]` is a number.
 * - MultiPolygon: `g[0]` is a polygon (array of rings), so `g[0][0]` is a Ring and `g[0][0][0]` is a Point (array).
 */
const isMartinezPolygon = (g: MartinezGeometry): g is MartinezPolygon => {
  if (!g.length) return false;
  const firstRing = g[0];
  if (!firstRing?.length) return false;
  const firstOfRing = firstRing[0];
  if (!Array.isArray(firstOfRing)) return false;
  // Numeric x → firstOfRing is Point [x, y] (Polygon). Otherwise firstOfRing is Ring (MultiPolygon).
  return typeof firstOfRing[0] === 'number';
};

/** Normalise a martinez result to an array of polygons. */
const toMartinezPolygons = (g: MartinezGeometry): MartinezPolygon[] =>
  isMartinezPolygon(g) ? [g] : (g as MartinezMultiPolygon);

/**
 * Convert a {@link Polygon2D} to a martinez {@link MartinezRing}.
 * Martinez requires closed rings (last point equals first point).
 */
const polygon2DToRing = (pts: Polygon2D): MartinezRing => {
  const ring: [number, number][] = pts.map(([x, y]) => [x, y]);
  const first = ring[0];
  const last = ring[ring.length - 1];
  if (first && last && (first[0] !== last[0] || first[1] !== last[1])) {
    ring.push([first[0], first[1]]);
  }
  return ring;
};

/**
 * Convert a martinez ring back to {@link Polygon2D}, stripping any closing point.
 * Returns `null` when the ring has fewer than 3 distinct points.
 */
const martinezRingToPolygon2D = (ring: MartinezRing): Polygon2D | null => {
  const last = ring[ring.length - 1];
  const first = ring[0];
  const open =
    last && first && last[0] === first[0] && last[1] === first[1]
      ? ring.slice(0, -1)
      : ring;
  if (open.length < 3) return null;
  return open as unknown as Polygon2D;
};

/** Next non-mezzanine {@link Storey} directly above the given storey, or `undefined`. */
const getStoreyAbove = (
  storeys: Storey[],
  storey: Storey,
): Storey | undefined => {
  const idx = storeys.findIndex((s) => s.id === storey.id);
  if (idx < 0) return undefined;
  for (let i = idx + 1; i < storeys.length; i++) {
    if (!storeys[i]?.mezzanine) return storeys[i];
  }
  return undefined;
};

/**
 * Check if any edge of ring A properly crosses any edge of ring B.
 * Shared endpoints (collinear overlap) are included — these are exactly
 * the degenerate configurations that cause martinez to loop infinitely.
 */
const doRingsShareOrCrossEdges = (
  a: MartinezRing,
  b: MartinezRing,
): boolean => {
  // Open rings: skip the closing segment duplicate if ring is already closed
  const aLen =
    a.length > 1 &&
    a[0]?.[0] === a[a.length - 1]?.[0] &&
    a[0]?.[1] === a[a.length - 1]?.[1]
      ? a.length - 1
      : a.length;
  const bLen =
    b.length > 1 &&
    b[0]?.[0] === b[b.length - 1]?.[0] &&
    b[0]?.[1] === b[b.length - 1]?.[1]
      ? b.length - 1
      : b.length;

  for (let i = 0; i < aLen; i++) {
    const a0 = a[i];
    const a1 = a[(i + 1) % aLen];
    if (!a0 || !a1) continue;

    for (let j = 0; j < bLen; j++) {
      const b0 = b[j];
      const b1 = b[(j + 1) % bLen];
      if (!b0 || !b1) continue;

      if (segmentsIntersect(a0, a1, b0, b1)) return true;
    }
  }
  return false;
};

/**
 * Self-intersection check for a closed {@link MartinezRing}.
 */
const isRingSelfIntersecting = (ring: MartinezRing): boolean => {
  const len =
    ring.length > 1 &&
    ring[0]?.[0] === ring[ring.length - 1]?.[0] &&
    ring[0]?.[1] === ring[ring.length - 1]?.[1]
      ? ring.length - 1
      : ring.length;
  if (len < 3) return false;

  for (let i = 0; i < len; i++) {
    const p1 = ring[i];
    const p2 = ring[(i + 1) % len];
    if (!p1 || !p2) continue;

    for (let j = i + 2; j < len; j++) {
      if (i === 0 && j === len - 1) continue;
      const q1 = ring[j];
      const q2 = ring[(j + 1) % len];
      if (!q1 || !q2) continue;

      if (segmentsIntersect(p1, p2, q1, q2)) return true;
    }
  }
  return false;
};

type SafeDiffResult =
  { valid: true; geometry: MartinezGeometry | null } | { valid: false };

/**
 * Safe wrapper around martinez {@link diff} that validates inputs before
 * calling the algorithm. Returns `{ valid: false }` when the inputs would
 * cause degenerate behaviour (infinite loop on self-intersecting or
 * shared-edge polygons), and `{ valid: true, geometry }` on success.
 * @param subject Current geometry to subtract from
 * @param clip Ring to subtract
 */
const safeDiff = (
  subject: MartinezGeometry,
  clip: MartinezRing,
): SafeDiffResult => {
  if (isRingSelfIntersecting(clip)) {
    return { valid: false };
  }

  const subjectPolygons = isMartinezPolygon(subject)
    ? [subject]
    : (subject as MartinezMultiPolygon);
  for (const polygon of subjectPolygons) {
    for (const ring of polygon) {
      if (isRingSelfIntersecting(ring)) {
        return { valid: false };
      }
      if (doRingsShareOrCrossEdges(ring, clip)) {
        return { valid: false };
      }
    }
  }

  return { valid: true, geometry: safeMartinezDiff(subject, [clip]) };
};

/**
 * Build roof polygons clipped to the area NOT covered by the storey above.
 * Only the exposed "air-facing" portion of the roof is returned.
 * @param storeys All storeys in the design
 * @param storey {@link Storey} to render
 * @returns Roof polygons (with possible holes) representing the exposed surface
 */
export const resolveStoreyRoofPolygons = (
  storeys: Storey[],
  storey: Storey,
): RoofPolygon[] => {
  if (storey.mezzanine) return [];
  const baseShapes = resolveStoreyFloorShapes(storeys, storey);
  if (!baseShapes.length) return [];

  // Roof earcut holes: cutout polygons whose centroid lies inside the given outer ring.
  const cutoutHoleRingsInside = (outer: Polygon2D): Polygon2D[] =>
    getCutoutShapes(storey)
      .filter(
        (cutout) =>
          isPolygon2D(cutout.points) &&
          isPointInPolygon(getShapeCenter(cutout), outer),
      )
      .map((cutout) => cutout.points);

  const above = getStoreyAbove(storeys, storey);
  const aboveRings: MartinezRing[] = above
    ? resolveStoreyFloorShapes(storeys, above)
        .filter((s) => isPolygon2D(s.points))
        .map((s) => polygon2DToRing(s.points as Polygon2D))
    : [];

  return baseShapes.flatMap((shape) => {
    if (!isPolygon2D(shape.points)) return [];

    // No storey above — full roof is exposed (include cutout holes in earcut rings).
    if (aboveRings.length === 0) {
      return [
        {
          id: shape.id,
          rings: [shape.points, ...cutoutHoleRingsInside(shape.points)],
        },
      ];
    }

    // Subtract each above-floor shape from the roof polygon.
    // safeDiff validates inputs before calling martinez to avoid infinite loops
    // on degenerate geometry (self-intersecting or shared edges). Falls back to
    // the full unclipped roof when validation fails or martinez throws.
    let current: MartinezGeometry | null = [polygon2DToRing(shape.points)];
    try {
      for (const aboveRing of aboveRings) {
        if (!current) break;
        const result = safeDiff(current, aboveRing);
        // Degenerate input detected — fall back to unclipped roof
        if (!result.valid) {
          return [
            {
              id: shape.id,
              rings: [shape.points, ...cutoutHoleRingsInside(shape.points)],
            },
          ];
        }
        current = result.geometry;
      }
    } catch {
      return [
        {
          id: shape.id,
          rings: [shape.points, ...cutoutHoleRingsInside(shape.points)],
        },
      ];
    }
    if (!current) return [];

    // Convert martinez result to RoofPolygon[]. A MultiPolygon result (disconnected
    // regions, e.g. L-shaped setback) produces one RoofPolygon entry per region.
    return toMartinezPolygons(current).flatMap((polygon, i): RoofPolygon[] => {
      const rings = polygon
        .map((ring) => martinezRingToPolygon2D(ring))
        .filter((r): r is Polygon2D => r !== null);
      if (rings.length === 0) return [];
      const outer = rings[0];
      if (!outer) return [];
      return [
        {
          id: `${shape.id}-${i}`,
          rings: [...rings, ...cutoutHoleRingsInside(outer)],
        },
      ];
    });
  });
};
